// Exchange Page - Currency Exchange
import { useState, useEffect } from "react";
import { motion, AnimatePresence } from "framer-motion";
import axios from "axios";
import { toast } from "sonner";
import { ArrowLeft, ArrowLeftRight, ArrowUpDown, ChevronDown, RefreshCw, Info } from "lucide-react";
import { SBER, API, pageVariants, tapAnimation } from "../utils/constants";
import { SberCard } from "../components/UIComponents";

const ExchangePage = ({ user, rate, onBack, onSuccess, t }) => {
  const [fromCurrency, setFromCurrency] = useState('USD');
  const [toCurrency, setToCurrency] = useState('KGS');
  const [amount, setAmount] = useState('');
  const [loading, setLoading] = useState(false);
  const [showFromPicker, setShowFromPicker] = useState(false);
  const [showToPicker, setShowToPicker] = useState(false);
  const [rates, setRates] = useState(null);
  const [ratesLoading, setRatesLoading] = useState(true);
  
  // Currency configuration
  const currencyInfo = {
    KGS: { name: 'Кыргызский сом', flag: '🇰🇬', symbol: 'с', bank: 'НБКР' },
    USD: { name: 'Доллар США', flag: '🇺🇸', symbol: '$', bank: 'FED' },
    EUR: { name: 'Евро', flag: '🇪🇺', symbol: '€', bank: 'ECB' },
    MDL: { name: 'Молдавский лей', flag: '🇲🇩', symbol: 'L', bank: 'BNM' },
    KZT: { name: 'Казахстанский тенге', flag: '🇰🇿', symbol: '₸', bank: 'НБРК' },
  };
  
  // Fetch rates from API
  useEffect(() => {
    const fetchRates = async () => {
      try {
        const res = await axios.get(`${API}/exchange/rates`);
        if (res.data.success) {
          setRates(res.data.rates);
        }
      } catch (err) {
        console.error('Failed to fetch rates:', err);
        setRates({ USD: 1, EUR: 1.08, KGS: 0.0114, KZT: 0.002, MDL: 0.055 });
      } finally {
        setRatesLoading(false);
      }
    };
    fetchRates();
  }, []);
  
  const getBalance = (currency) => {
    const key = `balance_${currency.toLowerCase()}`;
    return user?.[key] || 0;
  };
  
  const amountNum = parseFloat(amount) || 0;
  const fromBalance = getBalance(fromCurrency);
  
  const fromRate = rates?.[fromCurrency] || 1;
  const toRate = rates?.[toCurrency] || 1;
  const exchangeRate = fromRate / toRate;
  const fee = 1.5;
  const feeAmount = amountNum * (fee / 100);
  const netAmount = amountNum - feeAmount;
  const resultAmount = netAmount * exchangeRate;
  
  const handleSwap = () => {
    setFromCurrency(toCurrency);
    setToCurrency(fromCurrency);
    setAmount('');
  };
  
  const handleExchange = async () => {
    if (amountNum <= 0) {
      toast.error(t ? t('exchange.minAmount') : 'Введите сумму');
      return;
    }
    if (amountNum > fromBalance) {
      toast.error(t ? t('exchange.insufficientFunds') : 'Недостаточно средств');
      return;
    }
    if (fromCurrency === toCurrency) {
      toast.error(t ? t('errors.somethingWrong') : 'Выберите разные валюты');
      return;
    }
    
    setLoading(true);
    try {
      const res = await axios.post(`${API}/exchange`, {
        user_id: user?.user_id,
        from_currency: fromCurrency,
        to_currency: toCurrency,
        amount: amountNum
      });
      
      if (res.data.success) {
        toast.success(t ? t('exchange.exchangeSuccess') : `Обмен выполнен: +${res.data.to_amount.toFixed(2)} ${toCurrency}`);
        setAmount('');
        if (onSuccess) onSuccess();
      } else {
        toast.error(res.data.message || (t ? t('exchange.exchangeError') : 'Ошибка обмена'));
      }
    } catch (err) {
      toast.error(t ? t('exchange.exchangeError') : 'Ошибка обмена');
    } finally {
      setLoading(false);
    }
  };
  
  const CurrencyPicker = ({ selected, onSelect, onClose, exclude }) => (
    <motion.div 
      initial={{ opacity: 0 }}
      animate={{ opacity: 1 }}
      exit={{ opacity: 0 }}
      className="fixed inset-0 z-50 flex items-end justify-center"
      style={{ background: 'rgba(0,0,0,0.5)' }}
      onClick={onClose}
    >
      <motion.div 
        initial={{ y: 300 }}
        animate={{ y: 0 }}
        exit={{ y: 300 }}
        className="w-full max-w-md rounded-t-3xl p-5"
        style={{ background: SBER.white }}
        onClick={e => e.stopPropagation()}
      >
        <div className="w-12 h-1 rounded-full mx-auto mb-4" style={{ background: SBER.grayPale }} />
        <h3 className="text-[18px] font-semibold mb-4" style={{ color: SBER.dark }}>
          {t ? t('profile.language.select') : 'Выберите валюту'}
        </h3>
        <div className="space-y-2 max-h-[50vh] overflow-y-auto">
          {Object.entries(currencyInfo).map(([code, data]) => (
            <motion.button
              key={code}
              whileTap={tapAnimation}
              onClick={() => { onSelect(code); onClose(); }}
              disabled={code === exclude}
              className={`w-full p-4 rounded-xl flex items-center gap-3 ${code === exclude ? 'opacity-40' : ''}`}
              style={{ 
                background: selected === code ? SBER.greenPale : SBER.grayPale,
                border: selected === code ? `2px solid ${SBER.green}` : '2px solid transparent'
              }}
            >
              <span className="text-2xl">{data.flag}</span>
              <div className="flex-1 text-left">
                <p className="font-semibold" style={{ color: SBER.dark }}>{code}</p>
                <p className="text-[12px]" style={{ color: SBER.gray }}>{data.name}</p>
              </div>
              <div className="text-right">
                <p className="font-medium" style={{ color: SBER.dark }}>
                  {getBalance(code).toLocaleString('ru-RU', { minimumFractionDigits: 2 })}
                </p>
                <p className="text-[10px]" style={{ color: SBER.grayLight }}>{data.bank}</p>
              </div>
            </motion.button>
          ))}
        </div>
      </motion.div>
    </motion.div>
  );
  
  return (
    <motion.div 
      variants={pageVariants}
      initial="initial"
      animate="animate"
      exit="exit"
      className="min-h-screen pb-6"
      style={{ background: SBER.bg }}
    >
      <div className="px-5 pt-5 pb-4 flex items-center gap-3" style={{ background: SBER.white }}>
        <motion.button 
          whileTap={tapAnimation}
          onClick={onBack}
          className="w-10 h-10 rounded-full flex items-center justify-center"
          style={{ background: SBER.grayPale }}
        >
          <ArrowLeft className="w-5 h-5" style={{ color: SBER.gray }} />
        </motion.button>
        <h1 className="text-[18px] font-semibold" style={{ color: SBER.dark }}>
          {t ? t('exchange.title') : 'Обмен валют'}
        </h1>
      </div>
      
      <div className="px-5 pt-5">
        <SberCard className="p-4 mb-2">
          <p className="text-[12px] font-medium mb-2" style={{ color: SBER.gray }}>
            {t ? t('exchange.from') : 'Отдаёте'}
          </p>
          <div className="flex items-center gap-3">
            <motion.button
              whileTap={tapAnimation}
              onClick={() => setShowFromPicker(true)}
              className="flex items-center gap-2 px-3 py-2 rounded-xl"
              style={{ background: SBER.grayPale }}
            >
              <span className="text-xl">{currencyInfo[fromCurrency].flag}</span>
              <span className="font-semibold" style={{ color: SBER.dark }}>{fromCurrency}</span>
              <ChevronDown className="w-4 h-4" style={{ color: SBER.gray }} />
            </motion.button>
            <input
              type="number"
              value={amount}
              onChange={(e) => setAmount(e.target.value)}
              placeholder="0"
              className="flex-1 text-right text-[24px] font-bold bg-transparent outline-none"
              style={{ color: SBER.dark }}
              data-testid="exchange-from-amount"
            />
          </div>
          <div className="flex justify-between mt-2">
            <p className="text-[12px]" style={{ color: SBER.gray }}>
              {t ? t('exchange.insufficientFunds').split(' ')[0] : 'Доступно'}: {fromBalance.toLocaleString('ru-RU', { minimumFractionDigits: 2 })} {fromCurrency}
            </p>
            <motion.button
              whileTap={tapAnimation}
              onClick={() => setAmount(String(fromBalance))}
              className="text-[12px] font-semibold"
              style={{ color: SBER.green }}
            >
              MAX
            </motion.button>
          </div>
        </SberCard>
        
        <div className="flex justify-center -my-3 relative z-10">
          <motion.button
            whileTap={{ scale: 0.9, rotate: 180 }}
            onClick={handleSwap}
            className="w-12 h-12 rounded-full flex items-center justify-center shadow-lg"
            style={{ background: SBER.green }}
          >
            <ArrowUpDown className="w-5 h-5 text-white" />
          </motion.button>
        </div>
        
        <SberCard className="p-4 mb-4">
          <p className="text-[12px] font-medium mb-2" style={{ color: SBER.gray }}>
            {t ? t('exchange.to') : 'Получаете'}
          </p>
          <div className="flex items-center gap-3">
            <motion.button
              whileTap={tapAnimation}
              onClick={() => setShowToPicker(true)}
              className="flex items-center gap-2 px-3 py-2 rounded-xl"
              style={{ background: SBER.grayPale }}
            >
              <span className="text-xl">{currencyInfo[toCurrency].flag}</span>
              <span className="font-semibold" style={{ color: SBER.dark }}>{toCurrency}</span>
              <ChevronDown className="w-4 h-4" style={{ color: SBER.gray }} />
            </motion.button>
            <div className="flex-1 text-right">
              <span className="text-[24px] font-bold" style={{ color: SBER.green }}>
                {amountNum > 0 && !ratesLoading ? resultAmount.toFixed(2) : '0'}
              </span>
            </div>
          </div>
        </SberCard>
        
        {amountNum > 0 && !ratesLoading && (
          <SberCard className="p-4 mb-4">
            <div className="space-y-2">
              <div className="flex justify-between text-[13px]">
                <span style={{ color: SBER.gray }}>{t ? t('exchange.rate') : 'Курс'}</span>
                <span className="font-medium" style={{ color: SBER.dark }}>
                  1 {fromCurrency} = {exchangeRate.toFixed(4)} {toCurrency}
                </span>
              </div>
              <div className="flex justify-between text-[13px]">
                <span style={{ color: SBER.gray }}>{t ? t('topup.fee') : 'Комиссия'} ({fee}%)</span>
                <span className="font-medium" style={{ color: '#F59E0B' }}>
                  -{feeAmount.toFixed(2)} {fromCurrency}
                </span>
              </div>
            </div>
          </SberCard>
        )}
        
        <motion.button
          whileTap={tapAnimation}
          onClick={handleExchange}
          disabled={loading || amountNum <= 0 || fromCurrency === toCurrency}
          className="w-full py-4 rounded-xl font-semibold text-[16px] text-white disabled:opacity-50 flex items-center justify-center gap-2"
          style={{ background: SBER.green, boxShadow: '0 4px 16px rgba(33, 160, 56, 0.3)' }}
          data-testid="exchange-button"
        >
          {loading ? <RefreshCw className="w-5 h-5 animate-spin" /> : (
            <>
              <ArrowLeftRight className="w-5 h-5" />
              {t ? t('exchange.exchangeButton') : 'Обменять'}
            </>
          )}
        </motion.button>
        
        {ratesLoading && (
          <div className="mt-4 flex items-center justify-center gap-2 text-sm" style={{ color: SBER.gray }}>
            <RefreshCw className="w-4 h-4 animate-spin" />
            {t ? t('general.loading') : 'Загрузка курсов...'}
          </div>
        )}
      </div>
      
      <AnimatePresence>
        {showFromPicker && (
          <CurrencyPicker 
            selected={fromCurrency} 
            onSelect={setFromCurrency} 
            onClose={() => setShowFromPicker(false)}
            exclude={toCurrency}
          />
        )}
        {showToPicker && (
          <CurrencyPicker 
            selected={toCurrency} 
            onSelect={setToCurrency} 
            onClose={() => setShowToPicker(false)}
            exclude={fromCurrency}
          />
        )}
      </AnimatePresence>
    </motion.div>
  );
};

export default ExchangePage;
